import { type FC, useEffect } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router";
import { CardGridArea } from "widgets";

import { renderProteinAttributesWidgets } from "@/app-utils/attributes-widgets.tsx";

import { createSummaryCardsFromClassifications } from "../../app-utils/card.js";
import { useLoadDispatchWithIdAsNumber } from "../../app-utils/hooks/useLoadDispatchWithIdAsNumber.js";
import { type User } from "../../graphql.js";
import { type IProteinState, shouldLoadProtein } from "../../redux/modules/protein.js";
import { type IAppState } from "../../redux/reducer.js";
import { AttributesListWidget } from "../core/navigation-panel/AttributeWidget.js";
import { type IPageHeaderProps } from "../core/navigation-panel/PageHeader.js";
import BestHitCard from "./cards/BestHitCard.js";
import { DatabaseCard } from "./cards/DatabaseCard.js";
import { EcNumberListCard } from "./cards/EcNumberListCard.js";
import { GeneCard } from "./cards/GeneCard.js";
import { GenesCard } from "./cards/GenesCard.js";
import { GenomeCard } from "./cards/GenomeCard.js";
import { IntReferencesCard } from "./cards/IntReferencesCard.js";
import { LocationCard } from "./cards/LocationCard.js";
import { MetabolicPathwayListCard } from "./cards/MetabolicPathwayListCard.js";
import { ProteinCard } from "./cards/ProteinCard.js";
import { ProteinFeatureContentCard } from "./cards/ProteinFeatureContentCard.js";
import { ReferencesCard } from "./cards/ReferencesCard.js";
import { RelatedProjectsCard } from "./cards/RelatedProjectsCard.js";
import { TranscriptListCard } from "./cards/TranscriptListCard.js";
import GenericEntitySummaryView from "./GenericEntitySummaryView.js";

interface IProteinSummaryViewOwnProps {
  proteinId?: number; // In case the path is not "/protein/:proteinId" @see BlastResultHitSummaryView
  title?: string;
  pageHeaderProps?: IPageHeaderProps;
  blastContext?: boolean;
}

/*
 * The view inside of ProteinDetailPage. The children are cards for the current protein.
 */
const ProteinSummaryView: FC<IProteinSummaryViewOwnProps> = (props) => {
  const { proteinId } = useParams<"proteinId">();
  const { protein, isLoading } = useSelector<IAppState, IProteinState>((state) => state.protein);
  const currentUser = useSelector<IAppState, User>((state) => state.auth.session);
  const load = useLoadDispatchWithIdAsNumber(shouldLoadProtein, props.proteinId ?? proteinId);

  useEffect(() => {
    void load();
  }, [load]);

  const showSpinner: boolean = isLoading || !protein;
  const emptyMiddleCol =
    protein &&
    protein.allClassifications &&
    protein.allClassifications.length === 0 &&
    protein.ecNumbers &&
    protein.ecNumbers.length === 0 &&
    protein.literatureReferences &&
    protein.literatureReferences.length === 0 &&
    protein.allExternalReferences &&
    protein.allExternalReferences.length === 0;
  const dynamicColumnSwitch = emptyMiddleCol ? CardGridArea.Right : CardGridArea.Left;

  return (
    <GenericEntitySummaryView
      title={(props.title ? `${props.title}: ` : "") + (protein ? `${protein.sequenceId}` : "")}
      subtitle={protein?.description ?? "Protein Summary"}
      entity={isLoading ? null : protein}
      headerTags={props.pageHeaderProps?.tags}
      project={props.pageHeaderProps?.project}
      headerAttributes={[
        <AttributesListWidget
          attributes={renderProteinAttributesWidgets(protein)}
          key={"protein-summary-view-widget-1"}
        />,
      ]}
      isLoading={showSpinner}
      getEntityName={() => protein?.sequenceId}
    >
      {protein && (
        <>
          <ProteinCard protein={protein} area={CardGridArea.Left} blastContext={props.blastContext} />
          <LocationCard protein={protein} area={CardGridArea.Left} />
          {protein.genes && protein.genes.length > 1 ? (
            <GenesCard genes={protein.genes} area={dynamicColumnSwitch} title="Parent Genes" />
          ) : (
            <GeneCard gene={protein.genes[0]} area={dynamicColumnSwitch} title="Parent Gene" />
          )}

          <TranscriptListCard transcripts={protein.transcripts} area={dynamicColumnSwitch} title="Parent Transcripts" />
          <GenomeCard genome={protein.genome} area={CardGridArea.Left} />
          <DatabaseCard genome={protein.genome} database={protein.database} area={CardGridArea.Left} />
          <BestHitCard protein={protein} area={CardGridArea.Left} />
          {createSummaryCardsFromClassifications(protein.allClassifications, CardGridArea.Right)}
          <EcNumberListCard ecNumbers={protein.ecNumbers} area={CardGridArea.Right} ignoreIfEmpty={true} />
          <ProteinFeatureContentCard protein={protein} area={CardGridArea.Right} />
          <IntReferencesCard references={protein.internalReferences} area={CardGridArea.Right} />
          <ReferencesCard
            title="Literature References"
            references={protein.literatureReferences}
            area={CardGridArea.Right}
          />
          <MetabolicPathwayListCard
            pathways={protein.pathways}
            ignoreIfEmpty={true}
            area={CardGridArea.Right}
            title="Metabolic Pathways"
          />
          <ReferencesCard references={protein.allExternalReferences} area={CardGridArea.Right} />
          <RelatedProjectsCard
            relatedProjects={protein.relatedProjects}
            currentUser={currentUser}
            area={CardGridArea.Panel}
          />
        </>
      )}
    </GenericEntitySummaryView>
  );
};

export default ProteinSummaryView;
